import { useMemo, useState } from "react";
import {
  BarChart, Bar, Cell, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
} from "recharts";
import type { UserRowV2 } from "./sql";
import { buildHistograms, HOURLY_MAX_DAYS, FIFTEEN_MIN_MAX_DAYS, STAGES } from "./helpers";
import type { Granularity, StageHistogram, StageKey } from "./helpers";
import { PRIMARY, MUTED, FG1 } from "../../lib/funnel/theme";
import { SectionCardHeader } from "../../components/ChartCard";
import { fmtN } from "../../lib/funnel/helpers";
import { MultiChip } from "../../lib/funnel/filters";
import type { FilterPillSpec } from "../../components/FilterPills";

type Row = { label: string; count: number };
type Selection = { start: number; end: number } | null;
const UNSELECTED = "#C9C5BC";

function fmtMinute(m: number): string {
  const day = Math.floor(m / (60 * 24));
  const minOfDay = m % (60 * 24);
  const hr = Math.floor(minOfDay / 60);
  const mn = minOfDay % 60;
  return `d${day} ${String(hr).padStart(2, "0")}:${String(mn).padStart(2, "0")}`;
}

function bucketRangeLabel(granularity: Granularity, minuteStart: number, minuteEnd: number): string {
  if (granularity === "15min") {
    return `${fmtMinute(minuteStart)} – ${fmtMinute(minuteEnd + 1)} since signup`;
  }
  if (granularity === "hour") {
    const dayS = Math.floor(minuteStart / (60 * 24));
    const hrS = Math.floor((minuteStart % (60 * 24)) / 60);
    const dayE = Math.floor(minuteEnd / (60 * 24));
    const hrE = Math.floor((minuteEnd % (60 * 24)) / 60);
    if (dayS === dayE && hrS === hrE) return `Day ${dayS} · hour ${hrS}`;
    return `Day ${dayS} hr ${hrS} – Day ${dayE} hr ${hrE}`;
  }
  const dayStart = Math.floor(minuteStart / (60 * 24));
  const dayEnd = Math.floor(minuteEnd / (60 * 24));
  if (granularity === "week") {
    return dayStart === dayEnd ? `Day ${dayStart}` : `Days ${dayStart}–${dayEnd}`;
  }
  return dayStart === dayEnd ? `Day ${dayStart}` : `Day ${dayStart} – Day ${dayEnd}`;
}

function StageRow({
  hist,
  granularity,
  observationDays,
  height,
  hideFirstBucket,
}: {
  hist: StageHistogram;
  granularity: Granularity;
  observationDays: number;
  height: number;
  hideFirstBucket: boolean;
}) {
  const [selection, setSelection] = useState<Selection>(null);

  const visibleBuckets = useMemo(
    () => (hideFirstBucket ? hist.buckets.slice(1) : hist.buckets),
    [hist, hideFirstBucket],
  );
  const data: Row[] = useMemo(
    () => visibleBuckets.map((b) => ({ label: b.label, count: b.count })),
    [visibleBuckets],
  );

  const yMax = useMemo(() => {
    const m = Math.max(...data.map((d) => d.count), 1);
    return Math.ceil(m * 1.1);
  }, [data]);

  const reachedPct = hist.cohortTotal === 0
    ? 0
    : (hist.reachedCount / hist.cohortTotal) * 100;

  const xInterval = granularity === "week"
    ? 0
    : Math.max(0, Math.floor(data.length / 12));

  const isSelected = (i: number) =>
    selection != null && i >= selection.start && i <= selection.end;

  const handleBarClick = (idx: number, shiftKey: boolean) => {
    if (idx == null || idx < 0) return;
    setSelection((prev) => {
      if (shiftKey && prev) {
        return { start: Math.min(prev.start, idx), end: Math.max(prev.end, idx) };
      }
      if (prev && prev.start === idx && prev.end === idx) return null;
      return { start: idx, end: idx };
    });
  };

  const summary = useMemo(() => {
    if (!selection) return null;
    let total = 0;
    for (let i = selection.start; i <= selection.end; i++) {
      total += visibleBuckets[i]?.count ?? 0;
    }
    const startBucket = visibleBuckets[selection.start];
    const endBucket = visibleBuckets[selection.end];
    if (!startBucket || !endBucket) return null;
    const pctCohort = hist.cohortTotal === 0 ? 0 : (total / hist.cohortTotal) * 100;
    const pctReached = hist.reachedCount === 0 ? 0 : (total / hist.reachedCount) * 100;
    return {
      total,
      pctCohort,
      pctReached,
      label: bucketRangeLabel(granularity, startBucket.minuteStart, endBucket.minuteEnd),
      bucketCount: selection.end - selection.start + 1,
    };
  }, [selection, visibleBuckets, hist.cohortTotal, hist.reachedCount, granularity]);

  return (
    <div className="chart-tile" style={{ paddingBottom: 4 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 4, gap: 12 }}>
        <h3 style={{ fontSize: 13, fontWeight: 600, color: FG1, margin: 0, whiteSpace: "nowrap" }}>{hist.stage.label}</h3>
        <div style={{ fontSize: 11, color: "var(--fg-3)", textAlign: "right" }}>
          {fmtN(hist.reachedCount)} / {fmtN(hist.cohortTotal)} reached within {observationDays}d
          {" "}({reachedPct.toFixed(1)}%)
          <div style={{ fontSize: 10, color: "var(--fg-muted)", marginTop: 1 }}>
            Click a bar to select. Shift+click another bar to extend the range.
          </div>
        </div>
      </div>
      {summary && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            background: "rgba(47, 122, 138, 0.08)",
            border: "1px solid rgba(47, 122, 138, 0.25)",
            borderRadius: 6,
            padding: "4px 10px",
            marginBottom: 6,
            fontSize: 11,
            color: FG1,
          }}
        >
          <span>
            <strong>Selected:</strong> {summary.label}
            {summary.bucketCount > 1 ? ` · ${summary.bucketCount} buckets` : ""}
            {" · "}
            <strong>{fmtN(summary.total)}</strong> users
            {" · "}
            {summary.pctCohort.toFixed(2)}% of cohort ({fmtN(hist.cohortTotal)})
            {" · "}
            {summary.pctReached.toFixed(2)}% of those who reached ({fmtN(hist.reachedCount)})
          </span>
          <button
            type="button"
            onClick={() => setSelection(null)}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "var(--fg-3)",
              fontSize: 11,
              padding: 0,
            }}
            title="Clear selection"
          >
            ✕ clear
          </button>
        </div>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 4, right: 4, left: 4, bottom: 16 }} barCategoryGap={0}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 9, fill: MUTED }}
            interval={xInterval}
            tickLine={false}
            axisLine={{ stroke: "rgba(0,0,0,0.15)" }}
            height={16}
          />
          <YAxis
            tick={{ fontSize: 9, fill: MUTED }}
            width={32}
            domain={[0, yMax]}
            allowDecimals={false}
          />
          <Tooltip
            cursor={{ fill: "rgba(0,0,0,0.04)" }}
            formatter={(_v, _n, item) => {
              const r = item?.payload as Row | undefined;
              if (!r) return ["", ""] as [string, string];
              const pctCohort = hist.cohortTotal === 0 ? 0 : (r.count / hist.cohortTotal) * 100;
              const pctReached = hist.reachedCount === 0 ? 0 : (r.count / hist.reachedCount) * 100;
              return [
                `${fmtN(r.count)} users · ${pctCohort.toFixed(2)}% of cohort (${fmtN(hist.cohortTotal)}) · ${pctReached.toFixed(2)}% of those who reached this stage (${fmtN(hist.reachedCount)})`,
                "Users",
              ] as [string, string];
            }}
            labelFormatter={(label) => {
              const idx = data.findIndex((d) => d.label === label);
              if (idx < 0) return String(label);
              const b = visibleBuckets[idx];
              if (!b) return String(label);
              return bucketRangeLabel(granularity, b.minuteStart, b.minuteEnd);
            }}
          />
          <Bar
            dataKey="count"
            isAnimationActive={false}
            onClick={(payload, _idx, event) => {
              const label = (payload as { label?: string } | null)?.label;
              if (!label) return;
              const i = data.findIndex((d) => d.label === label);
              if (i < 0) return;
              const e = event as unknown as { shiftKey?: boolean };
              handleBarClick(i, !!e?.shiftKey);
            }}
            style={{ cursor: "pointer" }}
          >
            {data.map((_, i) => (
              <Cell key={i} fill={selection == null || isSelected(i) ? PRIMARY : UNSELECTED} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function tileHeightFor(count: number): number {
  if (count <= 1) return 260;
  if (count <= 2) return 180;
  if (count <= 4) return 130;
  return 90;
}

export function StageHistograms({
  users,
  granularity,
  onGranularityChange,
  observationDays,
  observationOptions,
  onObservationDaysChange,
  selectedStages,
  onSelectedStagesChange,
  pagePills,
}: {
  users: UserRowV2[];
  granularity: Granularity;
  onGranularityChange: (g: Granularity) => void;
  observationDays: number;
  observationOptions: number[];
  onObservationDaysChange: (d: number) => void;
  selectedStages: StageKey[];
  onSelectedStagesChange: (next: StageKey[]) => void;
  pagePills?: FilterPillSpec[];
}) {
  const [hideFirstBucket, setHideFirstBucket] = useState(false);
  const selectedSet = useMemo(() => new Set(selectedStages), [selectedStages]);
  const histograms = useMemo(
    () => buildHistograms(users, granularity, observationDays, selectedSet),
    [users, granularity, observationDays, selectedSet],
  );

  const cohortTotal = histograms[0]?.cohortTotal ?? 0;
  const hourlyAllowed = observationDays <= HOURLY_MAX_DAYS;
  const fifteenAllowed = observationDays <= FIFTEEN_MIN_MAX_DAYS;
  const tileHeight = tileHeightFor(histograms.length);

  const granularityLabel: Record<Granularity, string> = {
    "15min": "15-minute",
    hour: "hourly",
    day: "daily",
    week: "weekly",
  };
  const granularityNoun: Record<Granularity, string> = {
    "15min": "15-minute interval",
    hour: "hour",
    day: "day",
    week: "week",
  };

  const pills: FilterPillSpec[] = [
    ...(pagePills ?? []),
    { kind: "mode", label: `Buckets: ${granularityLabel[granularity]}` },
    { kind: "mode", label: `Window: first ${observationDays}d after signup` },
    { kind: "mode", label: `Events: ${selectedStages.length}/${STAGES.length}` },
    ...(hideFirstBucket ? [{ kind: "mode" as const, label: "First bucket hidden" }] : []),
  ];

  const stageOptions = STAGES.map((s) => ({ value: s.key, label: s.label }));

  return (
    <section className="card">
      <SectionCardHeader
        title="Time-to-stage distributions"
        subtitle={
          <>
            Each tile is one milestone; bars show how many users first hit it in that {granularityNoun[granularity]} since sign-up.
            X-axis is time since sign-up, 0 (left) to {observationDays} day{observationDays === 1 ? "" : "s"} (right). All {fmtN(cohortTotal)} users
            in the filter cohort are included; recent sign-ups simply have less elapsed window to hit later buckets.
          </>
        }
        filterPills={pills}
        headerExtra={
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <MultiChip
              label="Events"
              allLabel="All events"
              pluralNoun="events"
              options={stageOptions}
              selected={selectedStages.length === STAGES.length ? [] : selectedStages}
              onChange={(next) => onSelectedStagesChange(next.length === 0 ? STAGES.map((s) => s.key) : (next as StageKey[]))}
            />
            <div
              role="group"
              aria-label="Bucket size"
              style={{
                display: "inline-flex",
                border: `1px solid ${MUTED}`,
                borderRadius: 6,
                overflow: "hidden",
                fontSize: 11,
              }}
            >
              {(["15min", "hour", "day", "week"] as const).map((g) => {
                const active = granularity === g;
                const disabled =
                  (g === "hour" && !hourlyAllowed) ||
                  (g === "15min" && !fifteenAllowed);
                const title = disabled
                  ? g === "15min"
                    ? `15-minute buckets only available for the ${FIFTEEN_MIN_MAX_DAYS}-day window`
                    : `Hourly only available for windows up to ${HOURLY_MAX_DAYS} days`
                  : undefined;
                const label = g === "15min" ? "15 min" : g === "hour" ? "Hourly" : g === "day" ? "Daily" : "Weekly";
                return (
                  <button
                    key={g}
                    type="button"
                    onClick={() => { if (!disabled) onGranularityChange(g); }}
                    title={title}
                    style={{
                      padding: "3px 10px",
                      background: active ? PRIMARY : "transparent",
                      color: active ? "#fff" : disabled ? "var(--fg-muted)" : FG1,
                      border: "none",
                      cursor: disabled ? "not-allowed" : "pointer",
                      fontWeight: active ? 600 : 400,
                      opacity: disabled ? 0.5 : 1,
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <label
              className="filter-switch"
              title="Hide the first bucket so the y-axis rescales to the rest of the distribution."
              style={{ fontSize: 11 }}
            >
              <input
                type="checkbox"
                checked={hideFirstBucket}
                onChange={(e) => setHideFirstBucket(e.target.checked)}
              />
              <span className="track"><span className="thumb" /></span>
              <span className="label-text">Hide first bucket</span>
            </label>
            <span className="filter-chip" title="Time window after sign-up shown on the histogram x-axis." style={{ position: "relative" }}>
              <span className="chip-label">Window:</span>
              <span className="chip-value">{observationDays} day{observationDays === 1 ? "" : "s"}</span>
              <i className="ti ti-chevron-down chevron" />
              <select
                aria-label="Observation window"
                value={observationDays}
                onChange={(e) => onObservationDaysChange(Number(e.target.value))}
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  opacity: 0,
                  cursor: "pointer",
                  border: "none",
                  background: "transparent",
                }}
              >
                {observationOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt} day{opt === 1 ? "" : "s"}</option>
                ))}
              </select>
            </span>
          </div>
        }
      />
      <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {histograms.length === 0 ? (
          <div className="empty">No events selected. Use the Events picker above to choose at least one milestone.</div>
        ) : (
          histograms.map((h) => (
            <StageRow
              key={`${h.stage.key}|${granularity}|${observationDays}|${hideFirstBucket}`}
              hist={h}
              granularity={granularity}
              observationDays={observationDays}
              height={tileHeight}
              hideFirstBucket={hideFirstBucket}
            />
          ))
        )}
      </div>
    </section>
  );
}
